import {
  Settings,
  Briefcase,
  FileText,
  School,
  GraduationCap,
  CalendarDays,
  ListTodo,
  UserMinus,
  BarChart3,
  MapPin,
  MessageSquare,
  type LucideIcon,
} from 'lucide-react';
import { isFeatureEnabled, type FeatureKey } from './feature-flags';

export interface NavItem {
  /** Route path */
  path: string;
  /** Display label (German) */
  label: string;
  /** Lucide icon component */
  icon: LucideIcon;
  /** Roles that can see this item (if omitted, visible to all authenticated users) */
  allowedRoles?: string[];
  /** Feature flag key — item hidden when flag is false */
  featureKey?: FeatureKey;
}

/**
 * Alle Einträge der Hauptnavigation, vor dem Filtern nach Feature-Flags.
 *
 * **Die Reihenfolge ist der Weg durch einen Arbeitstag:** zuerst das ToDo —
 * was auf das Büro wartet —, dann Lehrkräfte und ihr Feedback, Schulen,
 * Angebote, Kurse, Vertretungen: wer, wo, was wir anbieten, was daraus wird,
 * wer einspringt.
 * Danach die Blicke von außen auf denselben Bestand (Karte, Auswertung),
 * zuletzt das Seltene (Karriere).
 *
 * **Warum Angebote einen eigenen Eintrag hat und kein Knopf auf der
 * Schulübersicht bleibt (16.09.2026):** Es ist die einzige Stelle im
 * Programm, an der **Interessenten-Schulen** vorkommen — Schulen, die noch
 * keine Kunden sind, leben ausschließlich im Angebot und stehen in keiner
 * Schulliste und in keinem Abgleich. Etwas, das sonst nirgends existiert,
 * gehört nicht hinter einen Knopf auf einer anderen Seite.
 *
 * **Warum Feedback einen eigenen Eintrag hat und kein Knopf auf der
 * Lehrkräfte-Seite bleibt (12.10.2026, Mails & Feedback s12):** Die Zahl
 * neuer Meldungen stand an „Lehrkräfte", ein Klick öffnete aber die
 * Lehrkräfte-Liste — die Zahl hing an etwas anderem als dem, was sie zählt.
 * Jetzt steht sie an dem Eintrag, der die Meldungen öffnet, und auf der
 * Feedback-Liste leuchtet nur dieser Eintrag (wie „Angebote" unter
 * „Schulen").
 *
 * `navigation.test.ts` hält sie fest; ohne das wird die Stelle, an der ein
 * neuer Eintrag landet, zufällig.
 */
const ALL_NAV_ITEMS: NavItem[] = [
  {
    // Der Korb der offenen Vorgänge: wer zugesagt hat, wer sich beworben
    // hat, was freizugeben, nachzubesetzen oder erneut einzuladen ist (Spec
    // 2026-09-20 D4). Ganz oben seit dem 21.09.2026 auf Wunsch des
    // Auftraggebers — das ist das Erste, was das Büro morgens aufschlägt;
    // vorher stand er zwischen Kurse und Vertretungen.
    path: '/todo',
    label: 'ToDo',
    icon: ListTodo,
    allowedRoles: ['admin', 'employee'],
    featureKey: 'schools',
  },
  {
    path: '/lehrkraefte',
    label: 'Lehrkräfte',
    icon: GraduationCap,
    allowedRoles: ['admin', 'employee'],
    featureKey: 'educators',
  },
  {
    // Gleicher Feature-Schalter wie „Lehrkräfte": ohne Lehrkräfte-Bereich
    // gibt es auch kein Lehrkräfte-Feedback. Pfad als Text — shared darf
    // FEEDBACK_PATH aus dem Feature nicht importieren.
    path: '/lehrkraefte/feedback',
    label: 'Feedback',
    icon: MessageSquare,
    allowedRoles: ['admin', 'employee'],
    featureKey: 'educators',
  },
  {
    path: '/schools',
    label: 'Schulen',
    icon: School,
    allowedRoles: ['admin', 'employee'],
    featureKey: 'schools',
  },
  {
    path: '/schools/angebote',
    label: 'Angebote',
    icon: FileText,
    allowedRoles: ['admin', 'employee'],
    featureKey: 'schools',
  },
  {
    path: '/courses',
    label: 'Kurse',
    icon: CalendarDays,
    allowedRoles: ['admin', 'employee'],
    featureKey: 'schools',
  },
  {
    path: '/substitutions',
    label: 'Vertretungen',
    icon: UserMinus,
    allowedRoles: ['admin', 'employee'],
    featureKey: 'schools',
  },
  {
    // Die dritte Leserichtung des Matchings: am Kurs "wer", an der Lehrkraft
    // "welche Kurse", hier "wo".
    //
    // Steht seit dem 10.09.2026 hinter den Vertretungen: Die vier Einträge
    // davor sind der Weg durch einen Arbeitstag — wer, wo, was, wer springt
    // ein. Karte und Auswertung sind Blicke von außen auf denselben Bestand.
    path: '/karte',
    label: 'Karte',
    icon: MapPin,
    allowedRoles: ['admin', 'employee'],
    featureKey: 'schools',
  },
  {
    // Keine Ansicht, sondern Zahlen — deshalb ein eigener Eintrag statt
    // eines Reiters in der Kursliste (Spec 10.4).
    path: '/courses/analytics',
    label: 'Auswertung',
    icon: BarChart3,
    allowedRoles: ['admin', 'employee'],
    featureKey: 'schools',
  },
  {
    // Steht bewusst am Ende: Die Einträge darüber sind das Tagesgeschäft,
    // die Stellenausschreibungen fasst man selten an.
    path: '/careers',
    label: 'Karriere',
    icon: Briefcase,
    allowedRoles: ['admin', 'employee'],
    featureKey: 'careers',
  },
];

/**
 * Bottom navigation item definitions (before filtering)
 */
const ALL_BOTTOM_NAV_ITEMS: NavItem[] = [
  {
    path: '/settings',
    label: 'Einstellungen',
    icon: Settings,
    allowedRoles: ['admin', 'employee'],
  },
];

/**
 * Get main navigation items filtered by feature flags. The role filter
 * (`allowedRoles`) is applied by the caller.
 */
export function getNavItems(): NavItem[] {
  return ALL_NAV_ITEMS.filter((item) => !item.featureKey || isFeatureEnabled(item.featureKey));
}

/**
 * Get bottom navigation items filtered by feature flags.
 */
export function getBottomNavItems(): NavItem[] {
  return ALL_BOTTOM_NAV_ITEMS.filter(
    (item) => !item.featureKey || isFeatureEnabled(item.featureKey)
  );
}

/**
 * Wie ein Bereich heißt — für alles, was ihn außerhalb der Navigation nennen
 * muss.
 *
 * Der Brotkrumen-Pfad (`shared/ui/breadcrumbs`) beschriftet damit sein erstes
 * Glied. Ohne diese Stelle stünde die Liste der Bereichsnamen ein zweites Mal
 * im Code, und die zweite liefe von der ersten weg: „Lehrkräfte" hier,
 * „Lehrer" dort, und niemand merkt es, weil beide für sich stimmen.
 *
 * **Ohne Rollenfilter, mit Absicht.** Die Frage lautet „wie heißt dieser
 * Bereich", nicht „darf jemand ihn sehen" — das entscheiden die
 * Routen-Wächter. Ein Filter hier ließe die Brotkrume auf einer Seite
 * verschwinden, die der Benutzer gerade offen hat.
 */
export function navLabelForPath(path: string): string | undefined {
  return [...ALL_NAV_ITEMS, ...ALL_BOTTOM_NAV_ITEMS].find((item) => item.path === path)?.label;
}
